import { useCallback, useRef, useState } from "react";

export const useAmbience = () => {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const stop = useCallback(() => {
    const nodes = nodesRef.current;
    const ctx = ctxRef.current;
    if (nodes && ctx) {
      nodes.stopCall();
      nodes.gain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.25);
      const toStop = nodes;
      setTimeout(() => {
        try {
          toStop.src.stop();
          toStop.lfo.stop();
        } catch {}
      }, 900);
      nodesRef.current = null;
    }
    setPlaying(false);
  }, []);

  const start = useCallback(() => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = ctxRef.current || new Ctx();
    ctxRef.current = ctx;
    if (ctx.state === "suspended") ctx.resume();

    // ocean = looped brown-ish noise through a lowpass, breathing via LFO
    const buf = ctx.createBuffer(1, ctx.sampleRate * 4, ctx.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 650;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.11;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.05;
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);
    src.connect(lp);
    lp.connect(gain);
    gain.connect(ctx.destination);
    src.start();
    lfo.start();
    gain.gain.setTargetAtTime(0.06, ctx.currentTime, 1);

    // occasional whale call = sine sweep
    let callTimer;
    const call = () => {
      if (!nodesRef.current) return;
      const o = ctx.createOscillator();
      const og = ctx.createGain();
      const t = ctx.currentTime;
      o.type = "sine";
      o.frequency.setValueAtTime(160, t);
      o.frequency.exponentialRampToValueAtTime(430, t + 0.9);
      o.frequency.exponentialRampToValueAtTime(130, t + 1.9);
      og.gain.setValueAtTime(0.0001, t);
      og.gain.exponentialRampToValueAtTime(0.03, t + 0.4);
      og.gain.exponentialRampToValueAtTime(0.0001, t + 2.3);
      o.connect(og);
      og.connect(ctx.destination);
      o.start(t);
      o.stop(t + 2.4);
      callTimer = setTimeout(call, 8000 + Math.random() * 9000);
    };
    callTimer = setTimeout(call, 2500);

    nodesRef.current = { src, lfo, gain, stopCall: () => clearTimeout(callTimer) };
    setPlaying(true);
  }, []);

  const toggle = useCallback(() => {
    if (playing) stop();
    else start();
  }, [playing, start, stop]);

  return { playing, toggle };
};
